import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './styles/globals.css';

import { ErrorBoundary } from './components/ErrorBoundary';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeedProvider } from './context/FeedContext';
import { PlatformLayout } from './components/layout/PlatformLayout';

// Retry wrapper for lazy imports — retries up to 2 times on chunk load failure
function lazyRetry(factory: () => Promise<any>, retries = 2): React.LazyExoticComponent<any> {
  return lazy(() =>
    factory().catch((err: any) => {
      if (retries > 0) {
        return new Promise<any>(resolve => setTimeout(resolve, 800)).then(() =>
          lazyRetry(factory, retries - 1) as any
        );
      }
      // Final failure — force reload to bust cache
      const reloaded = sessionStorage.getItem('chunk_reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
      }
      throw err;
    })
  );
}

// Helper for modules with named exports
function lazyNamed(factory: () => Promise<any>): React.LazyExoticComponent<any> {
  return lazyRetry(() => factory().then((m: any) => ({ default: m.default || Object.values(m)[0] })));
}

// Everything is lazy-loaded (no eager imports for pages)
const DistrictHome = lazyNamed(() => import('./pages/DistrictHome'));
const LoginPage = lazyNamed(() => import('./pages/LoginPage'));
const RegisterPage = lazyNamed(() => import('./pages/RegisterPage'));

const Market = lazyNamed(() => import('./pages/Market'));
const ProductDetail = lazyNamed(() => import('./pages/ProductDetail'));
const SellerDashboard = lazyNamed(() => import('./pages/SellerDashboard'));
const Culture = lazyNamed(() => import('./pages/CulturePage'));
const LearnPage = lazyNamed(() => import('./pages/LearnPage'));
const ArticlePage = lazyNamed(() => import('./pages/ArticlePage'));
const CommunityPage = lazyRetry(() => import('./pages/CommunityPage'));
const SoleMatesPage = lazyNamed(() => import('./pages/SoleMatesPage'));
const CreatorProfile = lazyNamed(() => import('./pages/CreatorProfile'));
const ProfileNew = lazyNamed(() => import('./pages/ProfileNew'));
const DistrictFeed = lazyRetry(() => import('./pages/DistrictFeed'));
const ChatSystem = lazyRetry(() => import('./pages/ChatSystem'));
const ForgotPasswordPage = lazyNamed(() => import('./pages/ForgotPasswordPage'));
const TermsPage = lazyNamed(() => import('./pages/TermsPage'));
const PrivacyPage = lazyNamed(() => import('./pages/PrivacyPage'));
const CartPage = lazyRetry(() => import('./pages/CartPage'));
const CheckoutSuccess = lazyNamed(() => import('./pages/CheckoutSuccess'));
const CreateListing = lazyRetry(() => import('./pages/CreateListing'));
const ShipOrder = lazyRetry(() => import('./pages/ShipOrder'));
const CreatePost = lazyRetry(() => import('./pages/CreatePost'));
const NewDrops = lazyRetry(() => import('./pages/NewDrops'));
const DropDetail = lazyRetry(() => import('./pages/DropDetail'));
const AdminImportProduct = lazyRetry(() => import('./pages/AdminImportProduct'));
const DistrictClips = lazyNamed(() => import('./pages/DistrictClips'));
const NotificationsPage = lazyNamed(() => import('./pages/NotificationsPage'));
const GamificationHub = lazyNamed(() => import('./pages/GamificationHub'));
const AdminDashboard = lazyNamed(() => import('./pages/AdminDashboard'));
const AboutDistrict = lazyNamed(() => import('./pages/AboutDistrict'));
const OrderHistory = lazyRetry(() => import('./pages/OrderHistory'));
const SettingsPage = lazyRetry(() => import('./pages/SettingsPage'));
const RefundPage = lazyRetry(() => import('./pages/RefundPage'));
const AuthenticationPolicyPage = lazyRetry(() => import('./pages/AuthenticationPolicyPage'));
const CreatorAgreementPage = lazyRetry(() => import('./pages/CreatorAgreementPage'));
const CommunityGuidelinesPage = lazyRetry(() => import('./pages/CommunityGuidelinesPage'));
const AcceptableUsePage = lazyRetry(() => import('./pages/AcceptableUsePage'));
const FeeDisclosurePage = lazyRetry(() => import('./pages/FeeDisclosurePage'));
const ShippingPolicyPage = lazyRetry(() => import('./pages/ShippingPolicyPage'));
const DisputeResolutionPage = lazyRetry(() => import('./pages/DisputeResolutionPage'));
const SellerAgreementPage = lazyRetry(() => import('./pages/SellerAgreementPage'));
const CounterfeitPolicyPage = lazyRetry(() => import('./pages/CounterfeitPolicyPage'));
const ChargebackPolicyPage = lazyRetry(() => import('./pages/ChargebackPolicyPage'));
const IdentityVerificationPage = lazyRetry(() => import('./pages/IdentityVerificationPage'));
const SellerPerformancePage = lazyRetry(() => import('./pages/SellerPerformancePage'));
const TrustSafetyPage = lazyRetry(() => import('./pages/TrustSafetyPage'));
const RiskReservePage = lazyRetry(() => import('./pages/RiskReservePage'));
const ModerationPolicyPage = lazyRetry(() => import('./pages/ModerationPolicyPage'));
const GovernanceCharterPage = lazyRetry(() => import('./pages/GovernanceCharterPage'));
const LiabilityDisclaimerPage = lazyRetry(() => import('./pages/LiabilityDisclaimerPage'));
const WalletTermsPage = lazyRetry(() => import('./pages/WalletTermsPage'));
const DataRetentionPage = lazyRetry(() => import('./pages/DataRetentionPage'));
const AIModerationPage = lazyRetry(() => import('./pages/AIModerationPage'));
const TaxReportingPage = lazyRetry(() => import('./pages/TaxReportingPage'));
const SecurityPolicyPage = lazyRetry(() => import('./pages/SecurityPolicyPage'));
const AdvertisingPolicyPage = lazyRetry(() => import('./pages/AdvertisingPolicyPage'));
const EmergencyResponsePage = lazyRetry(() => import('./pages/EmergencyResponsePage'));
const HowOrdersWorkPage = lazyRetry(() => import('./pages/HowOrdersWorkPage'));
const HowItWorksPage = lazyRetry(() => import('./pages/HowItWorksPage'));
const BuyingGuidePage = lazyRetry(() => import('./pages/BuyingGuidePage'));
const SellingGuidePage = lazyRetry(() => import('./pages/SellingGuidePage'));
const VerificationProcessPage = lazyRetry(() => import('./pages/VerificationProcessPage'));
const WhyTrustPage = lazyRetry(() => import('./pages/WhyTrustPage'));
const FAQPage = lazyRetry(() => import('./pages/FAQPage'));
const SomethingWrongPage = lazyRetry(() => import('./pages/SomethingWrongPage'));
const TrustCenterPage = lazyRetry(() => import('./pages/TrustCenterPage'));
const BuyerProtectionPage = lazyRetry(() => import('./pages/BuyerProtectionPage'));
const SellerProtectionPage = lazyRetry(() => import('./pages/SellerProtectionPage'));
const SecurePaymentsPage = lazyRetry(() => import('./pages/SecurePaymentsPage'));
const TransparencyPage = lazyRetry(() => import('./pages/TransparencyPage'));
const SupportCenterPage = lazyRetry(() => import('./pages/SupportCenterPage'));
const VerificationStoryPage = lazyRetry(() => import('./pages/VerificationStoryPage'));
const FounderLetterPage = lazyRetry(() => import('./pages/FounderLetterPage'));
const SafetyManifestoPage = lazyRetry(() => import('./pages/SafetyManifestoPage'));
const OrderTrackingPage = lazyRetry(() => import('./pages/OrderTrackingPage'));
const GrowthHubPage = lazyRetry(() => import('./pages/GrowthHubPage'));
const ReferralProgram = lazyNamed(() => import('./pages/ReferralProgram'));
const CreatorRevenuePage = lazyRetry(() => import('./pages/CreatorRevenuePage'));
const AffiliatePage = lazyRetry(() => import('./pages/AffiliatePage'));
const DropPartnershipsPage = lazyRetry(() => import('./pages/DropPartnershipsPage'));
const CampusAmbassadorPage = lazyRetry(() => import('./pages/CampusAmbassadorPage'));
const BrandCollabsPage = lazyRetry(() => import('./pages/BrandCollabsPage'));
const CreatorStorefrontsPage = lazyRetry(() => import('./pages/CreatorStorefrontsPage'));
const InfluencerProgramPage = lazyRetry(() => import('./pages/InfluencerProgramPage'));
const ShareRewardsPage = lazyRetry(() => import('./pages/ShareRewardsPage'));
const BetaAccessPage = lazyRetry(() => import('./pages/BetaAccessPage'));

// Emotional Hooks
const EmotionalHubPage = lazyRetry(() => import('./pages/EmotionalHubPage'));
const DreamGrailsPage = lazyRetry(() => import('./pages/DreamGrailsPage'));
const StyleQuizPage = lazyRetry(() => import('./pages/StyleQuizPage'));
const CollectionGalleryPage = lazyRetry(() => import('./pages/CollectionGalleryPage'));
const MoodBoardsPage = lazyRetry(() => import('./pages/MoodBoardsPage'));
const MemoryPostsPage = lazyRetry(() => import('./pages/MemoryPostsPage'));
const JourneyTimelinePage = lazyRetry(() => import('./pages/JourneyTimelinePage'));
const MilestonesPage = lazyRetry(() => import('./pages/MilestonesPage'));

// Intelligence / Power Features
const IntelligenceHubPage = lazyRetry(() => import('./pages/IntelligenceHubPage'));
const AIStylistPage = lazyRetry(() => import('./pages/AIStylistPage'));
const SmartPricingPage = lazyRetry(() => import('./pages/SmartPricingPage'));
const ListingInsightsPage = lazyRetry(() => import('./pages/ListingInsightsPage'));
const DemandMapPage = lazyRetry(() => import('./pages/DemandMapPage'));
const MarketTrendsPage = lazyRetry(() => import('./pages/MarketTrendsPage'));
const AutoTagsPage = lazyRetry(() => import('./pages/AutoTagsPage'));
const FraudDetectionPage = lazyRetry(() => import('./pages/FraudDetectionPage'));
const SmartNotificationsPage = lazyRetry(() => import('./pages/SmartNotificationsPage'));
const DynamicFeedPage = lazyRetry(() => import('./pages/DynamicFeedPage'));
const DiscoveryEnginePage = lazyRetry(() => import('./pages/DiscoveryEnginePage'));

// Existing unrouted pages
const AuctionHouse = lazyRetry(() => import('./pages/AuctionHouse'));
const ShippingCalculator = lazyRetry(() => import('./pages/ShippingCalculator'));
const VirtualTryOn = lazyRetry(() => import('./pages/VirtualTryOn'));

// New feature pages
const FlashSalesPage = lazyRetry(() => import('./pages/FlashSalesPage'));
const SalesForecastPage = lazyRetry(() => import('./pages/SalesForecastPage'));
const CompetitorMonitorPage = lazyRetry(() => import('./pages/CompetitorMonitorPage'));
const MarketingHubPage = lazyRetry(() => import('./pages/MarketingHubPage'));
const ItemQAPage = lazyRetry(() => import('./pages/ItemQAPage'));
const GeoListingsPage = lazyRetry(() => import('./pages/GeoListingsPage'));
const FeatureTogglesPage = lazyRetry(() => import('./pages/FeatureTogglesPage'));

// Power Pages
const VaultPage = lazyRetry(() => import('./pages/VaultPage'));
const DropRadarPage = lazyRetry(() => import('./pages/DropRadarPage'));
const DistrictLabPage = lazyRetry(() => import('./pages/DistrictLabPage'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const PageLoader: React.FC = () => {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto" />
        {slow && (
          <div className="mt-6">
            <p className="text-gray-500 text-sm mb-3">Taking longer than expected...</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-full transition-colors"
            >
              Reload
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  // Don't block on auth — show app immediately, auth loads in background
  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<PlatformLayout><PageLoader /></PlatformLayout>}>
        <Routes>
          <Route path="/" element={<Navigate to="/district" replace />} />

          <Route path="/district" element={<PlatformLayout><DistrictHome /></PlatformLayout>} />
          <Route path="/clips" element={<PlatformLayout><DistrictClips /></PlatformLayout>} />
          <Route path="/about" element={<PlatformLayout><AboutDistrict /></PlatformLayout>} />

          {/* Market */}
          <Route path="/market" element={<PlatformLayout><Market /></PlatformLayout>} />
          <Route path="/market/product/:id" element={<PlatformLayout><ProductDetail /></PlatformLayout>} />

          {/* Sell */}
          <Route path="/sell" element={<PlatformLayout><SellerDashboard /></PlatformLayout>} />
          <Route path="/sell/dashboard" element={<PlatformLayout><SellerDashboard /></PlatformLayout>} />
          <Route path="/sell/new" element={<PlatformLayout><CreateListing /></PlatformLayout>} />
          <Route path="/sell/ship" element={<PlatformLayout><ShipOrder /></PlatformLayout>} />

          {/* Drops */}
          <Route path="/drops" element={<PlatformLayout><NewDrops /></PlatformLayout>} />
          <Route path="/drops/:id" element={<PlatformLayout><DropDetail /></PlatformLayout>} />
          <Route path="/admin/import" element={<PlatformLayout><AdminImportProduct /></PlatformLayout>} />

          {/* Culture & Learn */}
          <Route path="/culture" element={<PlatformLayout><Culture /></PlatformLayout>} />
          <Route path="/learn" element={<PlatformLayout><LearnPage /></PlatformLayout>} />
          <Route path="/learn/:slug" element={<PlatformLayout><ArticlePage /></PlatformLayout>} />

          {/* Community & Social */}
          <Route path="/community" element={<PlatformLayout><CommunityPage /></PlatformLayout>} />
          <Route path="/feed" element={<PlatformLayout><DistrictFeed /></PlatformLayout>} />
          <Route path="/post/new" element={<PlatformLayout><CreatePost /></PlatformLayout>} />
          <Route path="/messages" element={<PlatformLayout><ChatSystem /></PlatformLayout>} />
          <Route path="/sole-mates" element={<PlatformLayout><SoleMatesPage /></PlatformLayout>} />

          {/* Creators */}
          <Route path="/creator/:id" element={<PlatformLayout><CreatorProfile /></PlatformLayout>} />
          <Route path="/creators" element={<PlatformLayout><Culture /></PlatformLayout>} />

          {/* User */}
          <Route path="/cart" element={<PlatformLayout><CartPage /></PlatformLayout>} />
          <Route path="/checkout/success" element={<PlatformLayout><CheckoutSuccess /></PlatformLayout>} />
          <Route path="/profile" element={<PlatformLayout><ProfileNew /></PlatformLayout>} />
          <Route path="/settings" element={<PlatformLayout><SettingsPage /></PlatformLayout>} />
          <Route path="/orders" element={<PlatformLayout><OrderHistory /></PlatformLayout>} />
          <Route path="/orders/:orderId" element={<PlatformLayout><OrderTrackingPage /></PlatformLayout>} />
          <Route path="/notifications" element={<PlatformLayout><NotificationsPage /></PlatformLayout>} />
          <Route path="/progress" element={<PlatformLayout><GamificationHub /></PlatformLayout>} />
          <Route path="/admin" element={<PlatformLayout><AdminDashboard /></PlatformLayout>} />

          {/* Auth */}
          <Route path="/login" element={<PlatformLayout><LoginPage /></PlatformLayout>} />
          <Route path="/register" element={<PlatformLayout><RegisterPage /></PlatformLayout>} />
          <Route path="/forgot-password" element={<PlatformLayout><ForgotPasswordPage /></PlatformLayout>} />

          {/* Legal */}
          <Route path="/terms" element={<PlatformLayout><TermsPage /></PlatformLayout>} />
          <Route path="/privacy" element={<PlatformLayout><PrivacyPage /></PlatformLayout>} />
          <Route path="/refunds" element={<PlatformLayout><RefundPage /></PlatformLayout>} />
          <Route path="/authentication-policy" element={<PlatformLayout><AuthenticationPolicyPage /></PlatformLayout>} />
          <Route path="/creator-agreement" element={<PlatformLayout><CreatorAgreementPage /></PlatformLayout>} />
          <Route path="/community-guidelines" element={<PlatformLayout><CommunityGuidelinesPage /></PlatformLayout>} />
          <Route path="/acceptable-use" element={<PlatformLayout><AcceptableUsePage /></PlatformLayout>} />
          <Route path="/fees" element={<PlatformLayout><FeeDisclosurePage /></PlatformLayout>} />
          <Route path="/shipping-policy" element={<PlatformLayout><ShippingPolicyPage /></PlatformLayout>} />
          <Route path="/dispute-resolution" element={<PlatformLayout><DisputeResolutionPage /></PlatformLayout>} />
          <Route path="/seller-agreement" element={<PlatformLayout><SellerAgreementPage /></PlatformLayout>} />
          <Route path="/counterfeit-policy" element={<PlatformLayout><CounterfeitPolicyPage /></PlatformLayout>} />
          <Route path="/chargeback-policy" element={<PlatformLayout><ChargebackPolicyPage /></PlatformLayout>} />
          <Route path="/identity-verification" element={<PlatformLayout><IdentityVerificationPage /></PlatformLayout>} />
          <Route path="/seller-performance" element={<PlatformLayout><SellerPerformancePage /></PlatformLayout>} />
          <Route path="/trust-safety" element={<PlatformLayout><TrustSafetyPage /></PlatformLayout>} />
          <Route path="/risk-reserve" element={<PlatformLayout><RiskReservePage /></PlatformLayout>} />
          <Route path="/moderation-policy" element={<PlatformLayout><ModerationPolicyPage /></PlatformLayout>} />
          <Route path="/governance" element={<PlatformLayout><GovernanceCharterPage /></PlatformLayout>} />
          <Route path="/liability-disclaimer" element={<PlatformLayout><LiabilityDisclaimerPage /></PlatformLayout>} />
          <Route path="/wallet-terms" element={<PlatformLayout><WalletTermsPage /></PlatformLayout>} />
          <Route path="/data-retention" element={<PlatformLayout><DataRetentionPage /></PlatformLayout>} />
          <Route path="/ai-moderation" element={<PlatformLayout><AIModerationPage /></PlatformLayout>} />
          <Route path="/tax-reporting" element={<PlatformLayout><TaxReportingPage /></PlatformLayout>} />
          <Route path="/security-policy" element={<PlatformLayout><SecurityPolicyPage /></PlatformLayout>} />
          <Route path="/advertising-policy" element={<PlatformLayout><AdvertisingPolicyPage /></PlatformLayout>} />
          <Route path="/emergency-response" element={<PlatformLayout><EmergencyResponsePage /></PlatformLayout>} />
          <Route path="/how-orders-work" element={<PlatformLayout><HowOrdersWorkPage /></PlatformLayout>} />
          <Route path="/how-it-works" element={<PlatformLayout><HowItWorksPage /></PlatformLayout>} />
          <Route path="/buying-guide" element={<PlatformLayout><BuyingGuidePage /></PlatformLayout>} />
          <Route path="/selling-guide" element={<PlatformLayout><SellingGuidePage /></PlatformLayout>} />
          <Route path="/verification-process" element={<PlatformLayout><VerificationProcessPage /></PlatformLayout>} />
          <Route path="/why-trust" element={<PlatformLayout><WhyTrustPage /></PlatformLayout>} />
          <Route path="/faq" element={<PlatformLayout><FAQPage /></PlatformLayout>} />
          <Route path="/something-wrong" element={<PlatformLayout><SomethingWrongPage /></PlatformLayout>} />

          {/* Trust Center */}
          <Route path="/trust" element={<PlatformLayout><TrustCenterPage /></PlatformLayout>} />
          <Route path="/buyer-protection" element={<PlatformLayout><BuyerProtectionPage /></PlatformLayout>} />
          <Route path="/seller-protection" element={<PlatformLayout><SellerProtectionPage /></PlatformLayout>} />
          <Route path="/secure-payments" element={<PlatformLayout><SecurePaymentsPage /></PlatformLayout>} />
          <Route path="/transparency" element={<PlatformLayout><TransparencyPage /></PlatformLayout>} />
          <Route path="/support" element={<PlatformLayout><SupportCenterPage /></PlatformLayout>} />
          <Route path="/verification-story" element={<PlatformLayout><VerificationStoryPage /></PlatformLayout>} />
          <Route path="/founder-letter" element={<PlatformLayout><FounderLetterPage /></PlatformLayout>} />
          <Route path="/safety-manifesto" element={<PlatformLayout><SafetyManifestoPage /></PlatformLayout>} />
          {/* Growth Engine */}
          <Route path="/grow" element={<PlatformLayout><GrowthHubPage /></PlatformLayout>} />
          <Route path="/referral" element={<PlatformLayout><ReferralProgram /></PlatformLayout>} />
          <Route path="/creator-revenue" element={<PlatformLayout><CreatorRevenuePage /></PlatformLayout>} />
          <Route path="/affiliates" element={<PlatformLayout><AffiliatePage /></PlatformLayout>} />
          <Route path="/drop-partnerships" element={<PlatformLayout><DropPartnershipsPage /></PlatformLayout>} />
          <Route path="/ambassadors" element={<PlatformLayout><CampusAmbassadorPage /></PlatformLayout>} />
          <Route path="/brand-collabs" element={<PlatformLayout><BrandCollabsPage /></PlatformLayout>} />
          <Route path="/creator-storefronts" element={<PlatformLayout><CreatorStorefrontsPage /></PlatformLayout>} />
          <Route path="/influencer-program" element={<PlatformLayout><InfluencerProgramPage /></PlatformLayout>} />
          <Route path="/share-rewards" element={<PlatformLayout><ShareRewardsPage /></PlatformLayout>} />
          <Route path="/beta-access" element={<PlatformLayout><BetaAccessPage /></PlatformLayout>} />

          {/* Emotional Hooks */}
          <Route path="/heart" element={<PlatformLayout><EmotionalHubPage /></PlatformLayout>} />
          <Route path="/dream-grails" element={<PlatformLayout><DreamGrailsPage /></PlatformLayout>} />
          <Route path="/style-quiz" element={<PlatformLayout><StyleQuizPage /></PlatformLayout>} />
          <Route path="/collection-gallery" element={<PlatformLayout><CollectionGalleryPage /></PlatformLayout>} />
          <Route path="/mood-boards" element={<PlatformLayout><MoodBoardsPage /></PlatformLayout>} />
          <Route path="/memory-posts" element={<PlatformLayout><MemoryPostsPage /></PlatformLayout>} />
          <Route path="/journey" element={<PlatformLayout><JourneyTimelinePage /></PlatformLayout>} />
          <Route path="/milestones" element={<PlatformLayout><MilestonesPage /></PlatformLayout>} />

          {/* Intelligence / Power Features */}
          <Route path="/intelligence" element={<PlatformLayout><IntelligenceHubPage /></PlatformLayout>} />
          <Route path="/ai-stylist" element={<PlatformLayout><AIStylistPage /></PlatformLayout>} />
          <Route path="/smart-pricing" element={<PlatformLayout><SmartPricingPage /></PlatformLayout>} />
          <Route path="/listing-insights" element={<PlatformLayout><ListingInsightsPage /></PlatformLayout>} />
          <Route path="/demand-map" element={<PlatformLayout><DemandMapPage /></PlatformLayout>} />
          <Route path="/market-trends" element={<PlatformLayout><MarketTrendsPage /></PlatformLayout>} />
          <Route path="/auto-tags" element={<PlatformLayout><AutoTagsPage /></PlatformLayout>} />
          <Route path="/fraud-detection" element={<PlatformLayout><FraudDetectionPage /></PlatformLayout>} />
          <Route path="/smart-notifications" element={<PlatformLayout><SmartNotificationsPage /></PlatformLayout>} />
          <Route path="/dynamic-feed" element={<PlatformLayout><DynamicFeedPage /></PlatformLayout>} />
          <Route path="/discovery" element={<PlatformLayout><DiscoveryEnginePage /></PlatformLayout>} />

          {/* Marketplace Features */}
          <Route path="/auctions" element={<PlatformLayout><AuctionHouse /></PlatformLayout>} />
          <Route path="/shipping-calculator" element={<PlatformLayout><ShippingCalculator /></PlatformLayout>} />
          <Route path="/virtual-tryon" element={<PlatformLayout><VirtualTryOn /></PlatformLayout>} />
          <Route path="/flash-sales" element={<PlatformLayout><FlashSalesPage /></PlatformLayout>} />
          <Route path="/item-qa" element={<PlatformLayout><ItemQAPage /></PlatformLayout>} />
          <Route path="/near-me" element={<PlatformLayout><GeoListingsPage /></PlatformLayout>} />

          {/* Seller Tools */}
          <Route path="/sales-forecast" element={<PlatformLayout><SalesForecastPage /></PlatformLayout>} />
          <Route path="/competitor-monitor" element={<PlatformLayout><CompetitorMonitorPage /></PlatformLayout>} />
          <Route path="/marketing-hub" element={<PlatformLayout><MarketingHubPage /></PlatformLayout>} />

          {/* Power Pages */}
          <Route path="/vault" element={<PlatformLayout><VaultPage /></PlatformLayout>} />
          <Route path="/drop-radar" element={<PlatformLayout><DropRadarPage /></PlatformLayout>} />
          <Route path="/lab" element={<PlatformLayout><DistrictLabPage /></PlatformLayout>} />

          {/* Admin */}
          <Route path="/admin/features" element={<PlatformLayout><FeatureTogglesPage /></PlatformLayout>} />

          <Route path="/fee-disclosure" element={<Navigate to="/fees" replace />} />
          <Route path="/shipping" element={<Navigate to="/shipping-policy" replace />} />

          {/* 404 */}
          <Route
            path="*"
            element={
              <PlatformLayout>
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-6xl font-bold text-district-red mb-4">404</h1>
                    <p className="text-xl text-gray-200 mb-8">This corner of the District doesn't exist yet.</p>
                    <a href="/district" className="btn-primary">Return to District</a>
                  </div>
                </div>
              </PlatformLayout>
            }
          />
        </Routes>
      </Suspense>
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <FeedProvider>
            <AppRoutes />
          </FeedProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
